import { NgTemplateOutlet } from "@angular/common";
import { Component, computed, effect, HostListener, inject, input, OnInit, output, untracked } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Subject, switchMap } from "rxjs";

import { AdaaHelper } from "../../../../core/utils";
import { Constants } from "../../../constants/constants";
import { PeriodModelType, SelectedPeriodModelType } from "../../../models";
import { PeriodSliderService, SystemLayoutService } from "../../../services";
import { PeriodRailComponent, PeriodRailStep } from "../../general/period-rail/period-rail";
import { ItemSliderComponent } from "../item-slider/item-slider";

@Component({
  selector: "adaa-period-slider",
  imports: [NgTemplateOutlet, PeriodRailComponent, ItemSliderComponent],
  templateUrl: "./period-slider.html",
  styleUrl: "./period-slider.scss",
})
export class PeriodSliderComponent implements OnInit {
  private readonly _systemLayoutService = inject(SystemLayoutService);
  private readonly _periodSliderService = inject(PeriodSliderService);
  private readonly _translateService = inject(TranslateService);

  periodId = input<number | undefined>(undefined);
  periodType = input<"quarter" | "year" | undefined>(undefined);
  activePeriod = input.required<SelectedPeriodModelType>();
  type = input<"quarter" | "year">("quarter");
  frequency = input<number>(Constants.FREQUENCY.MONTHLY);
  startDate = input<number | undefined>(undefined);
  endDate = input<number | undefined>(undefined);
  dontShowYears = input<boolean>(false);
  entityWeightsYears = input<Record<number, { weightIssue: boolean }>>();

  periodSelect = output<SelectedPeriodModelType>();

  selectedPeriod = computed(() => {
    const activePeriod = this.activePeriod() ?? this._periodSliderService.actualPeriod();
    if (activePeriod) {
      if (this.type() === "quarter" && activePeriod.type === "year") return activePeriod.period.year;
      return this.type() === "quarter" ? activePeriod.period.id : activePeriod.period.year;
    }
    return;
  });
  isReady = computed(() => this._periodSliderService.isReady());

  /**
   * @description
   *  What the rail highlights. A period landing between two steps — a semi-annual range
   *  keeps June and December while the actual period is rounded to September, a partial
   *  year holds no quarter close at all — matches no step id and would leave the rail
   *  blank, so it resolves to the step that closes over it. `selectedPeriod()` stays raw
   *  for `_shouldEmitPeriod()`, which still has to see a period the rail no longer holds.
   */
  selectedStepId = computed(() => {
    const selectedPeriod = this.selectedPeriod();
    if (!AdaaHelper.isDefined(selectedPeriod)) return undefined;
    if (this.type() !== "quarter") return selectedPeriod;

    const activePeriod = this.activePeriod() ?? this._periodSliderService.actualPeriod();
    if (activePeriod?.type === "year") return selectedPeriod;

    return this._resolveStepId(selectedPeriod, activePeriod!.period.year);
  });

  /**
   * @description
   *  Set while a whole year is the selection rather than one of its quarters, which is
   *  what the band emits. `selectedPeriod()` then holds the year instead of a period id,
   *  so no tick matches it and the band cell carries the selection on its own.
   */
  selectedYear = computed(() => {
    if (this.type() !== "quarter") return undefined;

    const activePeriod = this.activePeriod() ?? this._periodSliderService.actualPeriod();
    return activePeriod?.type === "year" ? activePeriod.period.year : undefined;
  });

  /**
   * @description
   *  A year is a band header on the rail, never a step, so the steps stay quarter ids.
   *  When a year holds a single step the band collapses and the step carries the year.
   */
  steps = computed<PeriodRailStep[]>(() => {
    const periodSlots = this._periodSliderService.periodSlots();

    if (this.type() === "year") {
      return periodSlots.map(({ year }) => ({ id: year, label: String(year), year }));
    }

    return periodSlots.flatMap(({ year, quarters }) =>
      quarters.map((quarter) => ({
        id: quarter.id,
        label: quarters.length > 1 ? `Q${quarter.quarter}` : String(year),
        year,
      }))
    );
  });

  yearIssues = computed<Record<number, string>>(() => {
    const entityWeightsYears = this.entityWeightsYears();
    if (!entityWeightsYears) return {};

    const issues: Record<number, string> = {};
    for (const [year, { weightIssue }] of Object.entries(entityWeightsYears)) {
      if (weightIssue) issues[+year] = this._translateService.instant("kpi_annual_weights.tooltips.period_kpi_issue");
    }
    return issues;
  });

  private readonly _untilDestroyed = AdaaHelper.untilDestroyed();
  private readonly _fetchPeriods$ = new Subject<void>();
  private _hasEmittedInitialPeriod = false;
  isMobile = window.innerWidth < 992;

  constructor() {
    this._fetchPeriods$
      .pipe(
        switchMap(() => this._periodSliderService.init(this.type(), this.frequency())),
        this._untilDestroyed()
      )
      .subscribe({
        next: () => this._initialize(),
      });

    effect(() => {
      const startDate = this.startDate();
      const endDate = this.endDate();

      untracked(() => this._fetchPeriods(startDate, endDate));
    });
  }

  @HostListener("window:resize")
  public onResize() {
    this.isMobile = window.innerWidth < 992;
  }

  public ngOnInit(): void {
    this._refresh();
  }

  private _fetchPeriods(startDate: number | undefined, endDate: number | undefined): void {
    const startYear = AdaaHelper.getDubaiTime(AdaaHelper.plan?.startYear);
    const endYear = AdaaHelper.getDubaiTime(AdaaHelper.plan?.endYear);

    this._periodSliderService.startDate.set(startDate ?? startYear);
    this._periodSliderService.endDate.set(endDate ?? endYear);
    this._fetchPeriods$.next();
  }

  public periodChanged(value: number): void {
    if (this.type() === "year") {
      this._yearlyPeriodChange(value);
      return;
    }
    this._quarterlyPeriodChange(value);
  }

  private _yearlyPeriodChange(value: number) {
    const period = this._periodSliderService.periodSlots().find((e) => e.year === value);
    const index = this.frequency() === Constants.FREQUENCY.ANNUAL ? 0 : 3;
    if (period) this._selectPeriod({ period: period.quarters[index], type: "year" });
  }

  private _quarterlyPeriodChange(value: number) {
    const allPeriods: PeriodModelType[] = [];
    this._periodSliderService.periodSlots().forEach((e) => allPeriods.push(...e.quarters));

    for (const item of allPeriods) {
      if (item.id === value) {
        this._selectPeriod({ period: item, type: "quarter" });
        return;
      }

      if (item.year === value && item.quarter === 4) {
        this._selectPeriod({ period: item, type: "year" });
        return;
      }
    }
  }

  private _initialize() {
    if (!this._shouldEmitPeriod()) return;
    this._hasEmittedInitialPeriod = true;

    const inheritedPeriod = this._getPeriod();
    if (inheritedPeriod) {
      this.periodSelect.emit({
        type: this.periodType() ?? this.type(),
        period: inheritedPeriod,
      });
      return;
    }

    const period = this._getFallbackPeriod();
    if (period) {
      try {
        this.periodSelect.emit(period);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {}
    }
  }

  /**
   * @description
   *  The actual period is today's, not the range's. A range closed before today — a
   *  project that ended in a past year — holds no period for it, so emitting it hands
   *  the parent an id the range never had: the page writes that id to the url and reads
   *  back an empty performance. It clamps to the nearest period the range does hold.
   */
  private _getFallbackPeriod(): SelectedPeriodModelType | undefined {
    const actualPeriod = this._periodSliderService.actualPeriod();
    if (!actualPeriod) return;

    const quarters = this._periodSliderService.periodSlots().flatMap(({ quarters }) => quarters);
    if (quarters.length === 0) return actualPeriod;

    const first = quarters[0];
    const last = quarters[quarters.length - 1];

    if (actualPeriod.period.id < first.id) return { ...actualPeriod, period: first };
    if (actualPeriod.period.id > last.id) return { ...actualPeriod, period: last };
    return actualPeriod;
  }

  /**
   * @description
   *  The initial period is emitted once, otherwise every range change would snap the
   *  parent back to the actual period. It is emitted again only when the range moved
   *  away from the selection, leaving the parent with a period the slider no longer has.
   */
  private _shouldEmitPeriod(): boolean {
    if (!this._hasEmittedInitialPeriod) return true;

    const selectedPeriod = this.selectedPeriod();
    if (!AdaaHelper.isDefined(selectedPeriod)) return true;

    return !this.steps().some(({ id, year }) => id === selectedPeriod || year === selectedPeriod);
  }

  /**
   * @description
   *  The period carried in from another screen. It is not always one of ours — a monthly
   *  screen hands over a plain month while this range holds quarter closes — so an id
   *  inside the range resolves forward to the quarter closing over it rather than being
   *  dropped, which used to lose the deep link and snap the page back to today. An id
   *  outside the range belongs to another plan and still falls back to the actual period.
   */
  private _getPeriod() {
    const periodId = Number(this.periodId());
    if (!periodId) return;

    const quarters = this._periodSliderService.periodSlots().flatMap(({ quarters }) => quarters);
    if (quarters.length === 0) return;

    const exact = quarters.find(({ id }) => id === periodId);
    if (exact) return exact;

    if (periodId < quarters[0].id) return;
    if (periodId > quarters[quarters.length - 1].id) return;

    return quarters.find(({ id }) => id >= periodId);
  }

  /**
   * @description
   *  A step closes the range it stands for, so a period sitting inside one resolves
   *  forward to it, or to the last step of its year when it sits past them all. A year
   *  the rail does not hold keeps the period untouched — it belongs to no step at all.
   */
  private _resolveStepId(periodId: number, year: number): number {
    const steps = this.steps();
    if (steps.some(({ id }) => id === periodId)) return periodId;

    const yearSteps = steps.filter((step) => step.year === year);
    if (yearSteps.length === 0) return periodId;

    const step = yearSteps.find(({ id }) => id >= periodId);
    return step?.id ?? yearSteps[yearSteps.length - 1].id;
  }

  private _selectPeriod(data: SelectedPeriodModelType) {
    this.periodSelect.emit(data);
  }

  private _refresh() {
    this._systemLayoutService.hasCycleChanged$
      .pipe(
        switchMap(() => this._periodSliderService.init(this.type(), this.frequency())),
        this._untilDestroyed()
      )
      .subscribe();

    this._periodSliderService.triggerRefresh$
      .pipe(
        switchMap(() => this._periodSliderService.init(this.type(), this.frequency())),
        this._untilDestroyed()
      )
      .subscribe();
  }
}
